/** Paramètres → E-mails : envoi, textes, rappels. */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { CheckField } from "../../components/CheckField";
import { Field } from "../../components/Field";
import { RichText } from "../../components/RichText";
import { useCompany } from "../../company/CompanyProvider";
import {
  MAIL_KINDS,
  MAIL_TOKENS,
  mailFromCompany,
  mailHourLabel,
  type CompanyMail,
  type MailKind,
} from "../../email/types";
import { EmailPreview } from "./EmailPreview";
import { isCompanyOwner } from "./owner";

type Pane = "send" | "texts" | "reminders";

export function EmailTab() {
  const { company, updateEmail } = useCompany();
  const [draft, setDraft] = useState<CompanyMail | null>(null);
  const [pane, setPane] = useState<Pane>("send");
  const [kind, setKind] = useState<MailKind>("invoice");
  const [into, setInto] = useState<"subject" | "body">("body");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;
    setDraft(mailFromCompany(company));
    setMsg("");
    setErr("");
  }, [company]);

  if (!company || !draft) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }

  const owner = isCompanyOwner(company);

  function set<K extends keyof CompanyMail>(key: K, value: CompanyMail[K]) {
    setDraft((cur) => (cur ? { ...cur, [key]: value } : cur));
  }

  function setTpl(patch: Partial<CompanyMail["templates"][MailKind]>) {
    setDraft((cur) => {
      if (!cur) return cur;
      return {
        ...cur,
        templates: { ...cur.templates, [kind]: { ...cur.templates[kind], ...patch } },
      };
    });
  }

  function insertToken(token: string) {
    const tpl = draft.templates[kind];
    if (into === "subject") {
      setTpl({ subject: `${tpl.subject}${token}`.slice(0, 180) });
      return;
    }
    const body = tpl.body || "";
    if (/<\/p>\s*$/i.test(body)) {
      setTpl({ body: body.replace(/<\/p>\s*$/i, `${token}</p>`) });
      return;
    }
    setTpl({ body: `${body}${token}` });
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await updateEmail(company.id, draft);
      setMsg("Enregistré.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={pane === "texts" ? "fx-mail" : "fx-co-card"}>
      <div className={pane === "texts" ? "fx-co-card" : undefined}>
        <header className="fx-co-card__head">
          <h2>E-mails</h2>
          <p>Textes et envoi — les pièces partent à la validation, les rappels à l’heure choisie.</p>
          {!owner ? (
            <p className="fx-co-form__hint">Lecture seule — seul le propriétaire enregistre.</p>
          ) : null}
        </header>
        <div className="fx-co-tabs" role="tablist" aria-label="E-mails">
          <button type="button" role="tab" aria-selected={pane === "send"} className={pane === "send" ? "is-on" : ""} onClick={() => setPane("send")}>
            Envoi
          </button>
          <button type="button" role="tab" aria-selected={pane === "texts"} className={pane === "texts" ? "is-on" : ""} onClick={() => setPane("texts")}>
            Textes
          </button>
          <button type="button" role="tab" aria-selected={pane === "reminders"} className={pane === "reminders" ? "is-on" : ""} onClick={() => setPane("reminders")}>
            Rappels
          </button>
        </div>

        {pane === "send" ? (
          <div className="fx-co-details fx-mail-pane">
            <Field label="Nom d’expéditeur" value={draft.from_name} disabled={!owner} placeholder={company.legal_name} onChange={(e) => set("from_name", e.target.value.slice(0, 80))} />
            <div className="fx-mail-pair">
              <Field label="Nom de réponse" value={draft.reply_name} disabled={!owner} onChange={(e) => set("reply_name", e.target.value.slice(0, 80))} />
              <Field label="Adresse de réponse" type="email" value={draft.reply_email} disabled={!owner} onChange={(e) => set("reply_email", e.target.value.slice(0, 255))} />
            </div>
            <Field
              label="CCI"
              value={draft.bcc}
              disabled={!owner}
              placeholder="scan@exemple.fr, compta@exemple.fr"
              onChange={(e) => set("bcc", e.target.value.slice(0, 400))}
            />
            <label className="fx-field flex flex-col gap-1 text-xs">
              Heure des rappels
              <select
                className="rounded-md border px-2.5 py-2 text-sm"
                value={draft.send_hour}
                disabled={!owner}
                onChange={(e) => set("send_hour", Number(e.target.value))}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {mailHourLabel(h)}
                  </option>
                ))}
              </select>
            </label>
            <CheckField label="Joindre le PDF" checked={draft.attach_pdf} disabled={!owner} onChange={(next) => set("attach_pdf", next)} />
            <p className="fx-co-form__hint">Sur une pièce, Envoyer valide et maille. Les récurrentes se règlent dans Flux et Paiements.</p>
          </div>
        ) : null}

        {pane === "texts" ? (
          <div className="fx-co-details fx-mail-pane">
            <div className="fx-mdl-skins" role="tablist" aria-label="Pièce">
              {MAIL_KINDS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  role="tab"
                  aria-selected={kind === k.id}
                  className={kind === k.id ? "is-on" : ""}
                  onClick={() => setKind(k.id)}
                >
                  {k.label}
                </button>
              ))}
            </div>
            <Field
              label="Sujet"
              value={draft.templates[kind].subject}
              disabled={!owner}
              onFocus={() => setInto("subject")}
              onChange={(e) => setTpl({ subject: e.target.value.slice(0, 180) })}
            />
            <div className="fx-mail-vars">
              <span>Variables — remplacées à l’envoi</span>
              <div className="fx-mail-tokens">
                {MAIL_TOKENS.map((t) => (
                  <button key={t.id} type="button" disabled={!owner} onClick={() => insertToken(t.id)}>
                    {t.label}
                  </button>
                ))}
              </div>
              <p className="fx-co-form__hint m-0">
                Insertion dans le {into === "subject" ? "sujet" : "corps"}. Cliquez le champ, puis une variable.
              </p>
            </div>
            <div onFocusCapture={() => setInto("body")}>
              <RichText
                label="Corps"
                value={draft.templates[kind].body}
                resetKey={`${company.id}-${kind}`}
                onChange={(html) => setTpl({ body: html })}
              />
            </div>
          </div>
        ) : null}

        {pane === "reminders" ? (
          <div className="fx-co-details fx-mail-pane">
            <p className="fx-co-form__hint m-0">Après l’échéance, à {mailHourLabel(draft.send_hour)}. Texte : onglet Rappel.</p>
            {draft.reminders.map((r, i) => (
              <div key={i} className="fx-mail-rem">
                <CheckField
                  label={`Rappel ${i + 1}`}
                  checked={r.enabled}
                  disabled={!owner}
                  onChange={(next) => {
                    const reminders = draft.reminders.map((x, j) => (j === i ? { ...x, enabled: next } : x));
                    set("reminders", reminders);
                  }}
                />
                <Field
                  label="Jours après échéance"
                  type="number"
                  min={1}
                  max={365}
                  value={String(r.days)}
                  disabled={!owner}
                  onChange={(e) => {
                    const days = Math.min(365, Math.max(1, Number(e.target.value) || 1));
                    const reminders = draft.reminders.map((x, j) => (j === i ? { ...x, days } : x));
                    set("reminders", reminders);
                  }}
                />
              </div>
            ))}
          </div>
        ) : null}

        {err ? <p className="fx-co-form__err">{err}</p> : null}
        {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
        <div className="fx-co-card__foot">
          <Button disabled={busy || !owner} onClick={() => void save()}>
            Enregistrer
          </Button>
        </div>
      </div>

      {pane === "texts" ? <EmailPreview mail={draft} kind={kind} company={company} /> : null}
    </div>
  );
}
